// Return "full results" for the search like including family info to patents, or members to families

import _ from 'lodash'
import { ClaimVersion } from '../claims/ClaimsProvider'
import { Claim } from '../claims/MemberClaimsProvider'
import { CostItem } from '../costs/CostsProvider'
import { family_member, patent_family } from '../data'
import { Family, Member } from '../patents/patents'
import { TaskProps } from '../tasks/utils'

type FullMember = ReturnType<typeof fullMember>

export function fullMember({
  member,
  familyById,
  costsByMemberId = {},
  claimsByMemberId = {},
  tasksLookUp = {},
}: {
  member: Member
  familyById: Record<number, Family>
  costsByMemberId?: Record<number, CostItem[]>
  claimsByMemberId?: Record<number, ClaimVersion[]>
  tasksLookUp?: Record<string, Record<number, TaskProps[]>> // entity -> entityId -> tasks
}) {
  const tasks = tasksLookUp[family_member]?.[member.familyMemberId] ?? []
  const claimVersions = claimsByMemberId[member.familyMemberId]
  let claims = []
  if (claimVersions) {
    claims = _(claimVersions).maxBy(v => v.versionDate).claims.map(sanitizeClaim)
  }
  return {
    ...sanitizeMember(member),
    family: sanitizeFamily(familyById[member.patentFamilyId]),
    costs: costsByMemberId[member.familyMemberId] ?? [],
    claims,
    tasks: tasks.map(sanitizeTask),
  }
}

type FullFamily = ReturnType<typeof fullFamily>

export function fullFamily({
  family,
  membersByFamilyId,
  tasksLookUp = {},
  costsByEntity = {},
} : {
  family: Family
  membersByFamilyId: Record<number, Member[]>
  tasksLookUp?: Record<string, Record<number, TaskProps[]>> // entity -> entityId -> tasks
  costsByEntity?: Record<string, Record<number, CostItem[]>>
}) {
  const members = membersByFamilyId[family.patentFamilyId] ?? []
  // only open tasks
  const tasks = [
    ...(tasksLookUp[patent_family]?.[family.patentFamilyId] ?? []),
    ...members.flatMap(m => tasksLookUp[family_member]?.[m.familyMemberId] ?? []),
  ].filter(t => !t.done).map(sanitizeTask)

  const costs = [
    ...(costsByEntity[patent_family]?.[family.patentFamilyId] ?? []),
    ...members.flatMap(m => costsByEntity[family_member]?.[m.familyMemberId] ?? []),
  ].map(sanitizeCost)

  return {
    ...sanitizeFamily(family),
    members: members.map(sanitizeMember),
    tasks,
    costs,
  }
}


export function fullMemberToString(full_member: FullMember) {

    return `${full_member.internalReference} ${full_member.externalReference ? '(' + full_member.externalReference + ')' : ''}
${full_member.title ? full_member.title : ''} 

Family: ${full_member.family.internalReference} ${full_member.family.externalReference ? '(' + full_member.family.externalReference + ')' : ''} ${full_member.family.familyName ? full_member.family.familyName : ''}
${full_member.family.summary ? full_member.family.summary : ''}
${full_member.family.priorityDate ? 'Priority Date' + full_member.family.priorityDate : ''}

Application: ${full_member.applicationNumber ?? 'N/A'} (Date: ${full_member.applicationDate ?? 'N/A'})
Publication: ${full_member.publicationNumber ?? 'N/A'} (Date: ${full_member.publicationDate ?? 'N/A'})
Patent: ${full_member.patentNumber ?? 'N/A'} (Date: ${full_member.patentDate ?? 'N/A'})
Status: ${full_member.familyMemberStatus} ${full_member.ipType ? full_member.ipType : ''} in ${full_member.countryCode}

Expiry: ${full_member.expiryDate ?? 'N/A'}; Abandonment: ${full_member.abandonmentDate ?? 'N/A'}

${['firstFiling', 'validated', 'pctRouteFiling'].map(k => full_member[k] === true ? k : undefined).filter(Boolean).join(', ')}

${full_member.claims?.length > 0 ? 'Claims:\n' + full_member.claims.map(claimToString).join('\n') : ''}

${full_member.tasks?.length > 0 ? 'Tasks:\n' + full_member.tasks.map(taskToString).join('\n') : ''}
    `
}

export function fullFamilyToString(full_family: FullFamily) {
  return `${full_family.internalReference} ${full_family.externalReference ? '(' + full_family.externalReference + ')' : ''}
${full_family.familyName ? full_family.familyName : ''}
${full_family.summary ? full_family.summary : ''}
${full_family.priorityDate ? 'Priority Date: ' + full_family.priorityDate : ''}

MEMBERS
${full_family.members.length === 0 ? 'No members' : full_family.members.map(m => smallMemberToString(m)).join('\n')}

TASKS
${full_family.tasks.length === 0 ? 'No tasks' : full_family.tasks.map(taskToString).join('\n')}
`
}

export function smallFamilyToString(family: Family) {
  return `${family.internalReference} ${family.externalReference ? '(' + family.externalReference + ')' : ''} ${family.familyName ? family.familyName : ''} ${family.priorityDate ? '- Priority Date: ' + family.priorityDate : ''}`
}

function smallMemberToString(member: Member) {
  return `${member.internalReference} ${member.externalReference ? '(' + member.externalReference + ')' : ''} ${member.familyMemberStatus} ${member.ipType ?? ''} in ${member.countryCode}`
}

export function longMemberToString(member: Member) {
  const ref = `Referenze: ${member.internalReference} ${member.externalReference ? '(extern' + member.externalReference + ')' : ''}`
  const what = `${member.familyMemberStatus} ${member.ipType ?? ''} in ${member.countryCode}`
  const application = `Application: ${member.applicationNumber ?? 'N/A'} (Date: ${member.applicationDate ?? 'N/A'})`
  const publication = member.publicationNumber ? `Publication: ${member.publicationNumber} (Date: ${member.publicationDate ?? 'N/A'})` : undefined
  const grant = member.patentNumber ? `Grant: ${member.patentNumber} (Date: ${member.patentDate ?? 'N/A'})` : undefined
  const expiry = member.expiryDate ? `Expiry: ${member.expiryDate}` : undefined
  const abandonment = member.abandonmentDate ? `Abandonment: ${member.abandonmentDate}` : undefined
  const end = [expiry, abandonment].filter(Boolean).join('; ')
  const tags = [
    member.firstFiling && 'First Filing',
    member.validated && 'Validated',
    member.pctRouteFiling && 'PCT Route Filing',
    member.unitaryPatent && 'Unitary Patent',
    member.optOut && 'Opted Out',
  ].filter(Boolean).join(', ')

  return [
    ref,
    what,
    application,
    publication,
    grant,
    end,
    tags,
  ].filter(Boolean).join('\n')
}

function claimToString(claim: Claim) {
  return `${claim.claimNumber} - ${claim.claimText}`
}

function taskToString(task: TaskProps) {
  return `${task.title} - ${task.comment ? task.comment + '.' : ''} Due ${task.dueDate}; internal due ${task.internalDueDate}; assigned to ${task.assignedTo}`
}

export function sanitizeMember(member: Member) {
    if (!member) return member

    const m = {...member}
    delete m.patentFamilyId
    delete m.familyMemberId
    return m
}

export function sanitizeFamily(family: Family) {
    if (!family) return family

    const f = {...family}
    delete f.patentFamilyId
    return f
}

export function sanitizeClaim(claim: Claim) {
    if (!claim) return claim

    const c = {...claim}
    delete c.claimId
    delete c.claimScopeId
    delete c.familyMemberId
    return c
}

export function sanitizeTask(task: TaskProps) {
    if (!task) return task

    const t = {...task}
    delete t.taskId
    delete t.entity
    delete t.entityId
    return t
}

export function sanitizeCost(cost: CostItem) {
    if (!cost) return cost

    const c = {...cost}
    delete c.costId
    // @ts-ignore
    delete c.entityId
    // @ts-ignore
    delete c.entity
    return c
}